import { useCallback, useEffect, useRef, useState } from 'react';
import { Add, CheckCircle, DeleteOutlined, RadioButtonUnchecked } from '@mui/icons-material';
import { toString } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { CompanyMeasurementDate } from 'api';
import { DELETE_MEASUREMENT_DATES_ENABLER } from 'common/constants/features';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import DeleteMeasurementDateDialog from 'components/DeleteMeasurementDate/DeleteMeasurementDateDialog';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { MeasurementDateDialog } from 'components/MeasurementDates';
import {
  PrimaryNavigationChip,
  PrimaryNavigationChipSkeleton,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
  Tooltip,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE, FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_TITLE } from 'pages/Firms/common/constants';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import useToggleMeasurementDateReady from 'services/hooks/useToggleMeasurementDateReady';
import { shortDate } from 'utilities';
import {
  PRIMARY_NAV_MENU_VISIBLE_ITEM_LIMIT,
  PRIMARY_NAVIGATION_MEASUREMENT_DATE_DISABLED_LABEL,
} from './PrimaryNavigation.constants';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from './PrimaryNavigation.testIds';
import { GUIDED_TOUR_TARGETS } from '../tours';

interface MeasurementDateChipProps {
  zoomLevel: number;
}

const MeasurementDateChip = ({ zoomLevel }: MeasurementDateChipProps) => {
  const {
    selectedMeasurementDate,
    companyMeasurementDates,
    isLoadingMeasurementDates,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    updateMeasurementDate,
    refreshFirmMeasurementDates,
  } = useFirmMeasurementDateFilter();
  const [{ companyInfo, companyList, user }] = useStore() as UseStoreValues;
  const { canCreateMeasurementDate, canDeleteMeasurementDate } = useFirmScopedPermissions();
  const [, checkFeature] = useCheckFeatures();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mdDialogOpen, setMdDialogOpen] = useState(false);
  const [deleteMdDialogOpen, setDeleteMdDialogOpen] = useState(false);
  const [mdHasBeenDeleted, setMdHasBeenDeleted] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const {
    canShowFewerOptions,
    hasMoreOptions,
    hiddenOptionCount: hiddenMeasurementDateCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions: visibleDates,
  } = useSearchableLimitedOptions({
    getSearchText: measurementDate => `${measurementDate.name} ${measurementDate.date}`,
    options: companyMeasurementDates,
    visibleLimit: PRIMARY_NAV_MENU_VISIBLE_ITEM_LIMIT,
  });
  const navigate = useNavigate();
  const location = useLocation();

  // companyInfo is set asynchronously by FirmBaseLayout and initialises as {}.
  // Guard against the id-less empty object so the dialog only receives a real company.
  const activeCompany = companyInfo?.id ? companyInfo : null;
  const hasNoCompanies = Array.isArray(companyList) && companyList.length === 0;
  const mostRecentDateLabel = shortDate(mostRecentMeasurementDate?.date);
  const mostRecentLabel = mostRecentDateLabel
    ? `${FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_TITLE} (${mostRecentDateLabel})`
    : FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_TITLE;
  const selectedLabel = isMostRecentMeasurementDate || !selectedMeasurementDate
    ? mostRecentLabel
    : shortDate(selectedMeasurementDate.date) ?? selectedMeasurementDate.name;
  const canOpenMeasurementDateMenu =
    !isLoadingMeasurementDates
    && !hasNoCompanies
    && (companyMeasurementDates.length > 0 || canCreateMeasurementDate);
  const canShowDeleteMeasurementDateAction = canDeleteMeasurementDate
    && companyMeasurementDates.length > 0
    && checkFeature(DELETE_MEASUREMENT_DATES_ENABLER);
  const isMeasurementDateChipDisabled = !isLoadingMeasurementDates && !canOpenMeasurementDateMenu;

  const isStaffOrSuperuser = Boolean(user?.is_staff || user?.is_superuser);

  const refreshDates = useCallback(async () => {
    await refreshFirmMeasurementDates({ companyId: activeCompany?.id });
  }, [activeCompany?.id, refreshFirmMeasurementDates]);

  const { handleToggleReady } = useToggleMeasurementDateReady({ refreshDates });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (canOpenMeasurementDateMenu) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    resetSearchableOptions();
  };

  const handleSelect = (measurementDateId: string) => {
    handleClose();
    updateMeasurementDate({ measurementDateId });
  };

  const handleAddMeasurementDate = () => {
    handleClose();
    setMdDialogOpen(true);
  };

  const handleDeleteMeasurementDate = () => {
    handleClose();
    setDeleteMdDialogOpen(true);
  };

  useEffect(() => {
    if (mdHasBeenDeleted) {
      navigate(location.pathname);
      setMdHasBeenDeleted(false);
    }
  }, [mdHasBeenDeleted, navigate, location.pathname]);

  const getMeasurementDates = useCallback(
    async (newCompanyMeasurementDate?: CompanyMeasurementDate) => {
      if (!activeCompany?.id) {
        return;
      }
      await refreshFirmMeasurementDates({ companyId: activeCompany.id });
      const newSlug = newCompanyMeasurementDate?.measurement_date?.slug;
      if (newSlug) {
        navigate(`${location.pathname}?date=${newSlug}`);
      }
    },
    [activeCompany, location.pathname, navigate, refreshFirmMeasurementDates]
  );

  const measurementDateChip = (
    <PrimaryNavigationChip
      label="Measurement Date"
      onClick={canOpenMeasurementDateMenu ? handleOpen : undefined}
      aria-busy={isLoadingMeasurementDates || undefined}
      aria-disabled={isMeasurementDateChipDisabled || undefined}
      aria-haspopup={canOpenMeasurementDateMenu ? 'listbox' : undefined}
      data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.measurementDateChip}
      data-tour={GUIDED_TOUR_TARGETS.measurementDateSelector}
      showDropdownIcon={canOpenMeasurementDateMenu}
      value={isLoadingMeasurementDates ? (
        <PrimaryNavigationChipSkeleton width={92} zoomLevel={zoomLevel} />
      ) : selectedLabel}
      valueTone={isMostRecentMeasurementDate ? 'highlight' : 'strong'}
      zoomLevel={zoomLevel}
    />
  );

  let measurementDateChipWithTooltip = measurementDateChip;
  if (hasNoCompanies && !isLoadingMeasurementDates) {
    measurementDateChipWithTooltip = (
      <Tooltip placement="bottom" title={PRIMARY_NAVIGATION_MEASUREMENT_DATE_DISABLED_LABEL}>
        {measurementDateChip}
      </Tooltip>
    );
  }

  return (
    <>
      {measurementDateChipWithTooltip}
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.measurementDateMenu}
        disableAutoFocusItem
        menuMinWidth={200}
        zoomLevel={zoomLevel}
        scrollHintItemLabelPlural="measurement dates"
        scrollHintItemLabelSingular="measurement date"
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{
          disableListWrap: true,
        }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear measurement date search"
          inputRef={searchInputRef}
          value={searchValue}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Find a Measurement Date"
          fullWidth
        />
        {visibleDates.map(date => {
          // cmd_id is present at runtime from MeasurementDateNameSerializer but not reflected
          // in CompanyMeasurementDateName's generated type; the cast is a known type-layer gap.
          const cmdId = (date as { cmd_id?: number }).cmd_id;
          const isReady = date.is_ready;
          const isMostRecentOption = date.id === FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE.id;
          const optionLabel = isMostRecentOption
            ? mostRecentLabel
            : shortDate(date.date) ?? date.name;
          const readyIcon = isStaffOrSuperuser && cmdId ? (
            <Tooltip title={isReady ? 'Mark as Not Ready' : 'Mark as Ready'} placement="right">
              <PrimaryNavigationMenuItemLeadingAction
                aria-label={isReady ? 'Mark as Not Ready' : 'Mark as Ready'}
                onClick={e => handleToggleReady(cmdId, isReady, e)}>
                {isReady ? (
                  <CheckCircle fontSize="small" color="success" />
                ) : (
                  <RadioButtonUnchecked fontSize="small" color="warning" />
                )}
              </PrimaryNavigationMenuItemLeadingAction>
            </Tooltip>
          ) : undefined;

          return (
            <PrimaryNavigationMenuItem
              key={date.id}
              isActive={date.id === selectedMeasurementDate?.id}
              onClick={() => handleSelect(toString(date.id))}
              endIcon={readyIcon}
              data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.measurementDateOption(date.id)}>
              {optionLabel}
            </PrimaryNavigationMenuItem>
          );
        })}
        {visibleDates.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            No measurement dates found
          </PrimaryNavigationMenuItem>
        )}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenMeasurementDateCount}
            itemLabelPlural="measurement dates"
            itemLabelSingular="measurement date"
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural="measurement dates"
            itemLabelSingular="measurement date"
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {canCreateMeasurementDate && (
          <PrimaryNavigationMenuAction
            icon={<Add />}
            onClick={handleAddMeasurementDate}
            data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.addMeasurementDate}>
            Add Measurement Date
          </PrimaryNavigationMenuAction>
        )}
        {canShowDeleteMeasurementDateAction && (
          <PrimaryNavigationMenuAction
            icon={<DeleteOutlined />}
            onClick={handleDeleteMeasurementDate}
            tone="danger"
            data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.deleteMeasurementDate}>
            Delete Measurement Date
          </PrimaryNavigationMenuAction>
        )}
      </PrimaryNavigationMenu>
      {mdDialogOpen && (
        <MeasurementDateDialog
          open={mdDialogOpen}
          onClose={() => setMdDialogOpen(false)}
          activeCompany={activeCompany}
          getMeasurementDates={getMeasurementDates}
        />
      )}
      {deleteMdDialogOpen && (
        <DeleteMeasurementDateDialog
          open={deleteMdDialogOpen}
          onClose={() => setDeleteMdDialogOpen(false)}
          getMeasurementDates={getMeasurementDates}
          setMdHasBeenDeleted={setMdHasBeenDeleted}
        />
      )}
    </>
  );
};

export default MeasurementDateChip;
